import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { X, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal that lets a teacher push a specific question to one of their classes
 * as an assignment.
 *
 * Props:
 *   - source: 'question_bank' | 'create' | 'manual'
 *   - subject: 'Business' | 'Economics' | 'Geography'
 *   - question_text, mark_scheme, marks (optional)
 *   - paper_year, series, question_number (optional)
 *   - title (short label)
 *   - onClose, onAssigned
 */
export default function AssignToClassModal({
    source = 'manual', subject, level = 'IAL',
    question_text = '', mark_scheme = '', marks = null,
    paper_year = '', series = '', question_number = '', title = '',
    onClose, onAssigned,
}) {
    const [classes, setClasses] = useState(null);
    const [classroomId, setClassroomId] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/api/teacher/classrooms').then((r) => {
            const all = (r.data || []).filter((c) => !c.archived);
            // Show all matching subject+level classes first, then others
            const matching = all.filter((c) => c.subject === subject && (c.level || 'IAL') === level);
            const others = all.filter((c) => !(c.subject === subject && (c.level || 'IAL') === level));
            const list = [...matching, ...others];
            setClasses(list);
            setClassroomId(matching[0]?.id || all[0]?.id || '');
        }).catch(() => setClasses([]));
    }, [subject, level]);

    const submit = async (e) => {
        e.preventDefault();
        if (!classroomId) return toast.error('Choose a class first');
        setSaving(true);
        try {
            await api.post(`/api/teacher/classrooms/${classroomId}/assignments`, {
                subject,
                level,
                title,
                question_text,
                mark_scheme,
                marks,
                paper_year, series, question_number,
                source,
                due_date: dueDate ? new Date(dueDate).toISOString() : null,
            });
            toast.success('Assignment sent to class');
            onAssigned?.();
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not assign');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-to-class-modal">
            <div className="bg-white rounded-3xl border-2 border-zinc-950 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-black inline-flex items-center gap-2"><Send className="w-5 h-5 text-[#7C3AED]" /> Assign to class</h3>
                    <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg" data-testid="assign-close"><X className="w-4 h-4" /></button>
                </div>

                {classes === null ? (
                    <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-[#7C3AED] animate-spin" /></div>
                ) : classes.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-4 text-center">You don't have any active classes yet. Create one first.</p>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        {title && (
                            <div className="px-3 py-2 rounded-lg bg-[#F8F7FC] border border-zinc-200">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">{subject}{marks ? ` · ${marks} marks` : ''}</div>
                                <div className="text-sm font-bold line-clamp-2">{title}</div>
                            </div>
                        )}
                        <label className="block">
                            <span className="text-xs font-bold text-zinc-600 mb-1.5 block">Class</span>
                            <select value={classroomId} onChange={(e) => setClassroomId(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" data-testid="assign-classroom-select">
                                {classes.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.subject}{c.level ? ` · ${c.level}` : ''}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-bold text-zinc-600 mb-1.5 block">Due date (optional)</span>
                            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none text-sm" data-testid="assign-due-date" />
                        </label>
                        <button type="submit" disabled={saving} className="w-full bg-[#7C3AED] text-white font-bold text-sm py-3 rounded-xl hover:bg-[#6D28D9] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="assign-submit">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {saving ? 'Assigning…' : 'Assign to class'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
